import { FormStore, GlobalStore } from '@roc/feature-app-core';
import { AppraisalsService } from '@roc/feature-appraisals';
import {
  DateFormat,
  DescribedRennovation,
  formatDate,
  generateUUID,
  GENERIC_ERROR_MESSAGE,
  NEW_CONSTRUCTION,
  parseAddress,
  propertyCitiesRestriction,
  PropertyOwnership,
  PURCHASE,
  REFINANCE,
  statePropertyTypeRestriction
} from '@roc/feature-utils';

import { isNil } from '@roc/ui/utils';
import { MSAService } from 'libs/feature-loans/src/loanSubmission/services/msaService';
import {
  computed,
  flow,
  makeObservable,
  observable,
  override
} from 'mobx';
import { LoanSubType, PROPERTY_TYPE_OTHER, propertyTypeOptions, propertyTypeOptionsFnFGU } from '../../utils/constants';
import { getUnitsByPropertyType } from '../../utils/utils';

const RenoDictionary = {
  NO: DescribedRennovation.NO,
  HORIZONTAL: DescribedRennovation.HORIZONTAL,
  VERTICAL: DescribedRennovation.VERTICAL,
  VERTICAL_AND_HORIZONTAL: DescribedRennovation.VERTICAL_AND_HORIZONTAL,
  [DescribedRennovation.CONDO_CONVERSION]:
    DescribedRennovation.CONDO_CONVERSION,
  [DescribedRennovation.UNIT_COUNT]: DescribedRennovation.UNIT_COUNT,
  [DescribedRennovation.ADU_CONVERSION]: DescribedRennovation.ADU_CONVERSION,
  [DescribedRennovation.FIRE_DAMAGE]: DescribedRennovation.FIRE_DAMAGE,
  [DescribedRennovation.CONVERSION_EXISTING_SPACE]:
    DescribedRennovation.CONVERSION_EXISTING_SPACE,
};

export class PropertyFormStore extends FormStore {
  private globalStore: GlobalStore;
  private appraisalService: AppraisalsService;
  public loanSubType: string;
  private msaService = new MSAService();

  key = generateUUID();
  isEditing = true;

  constructor(form, globalStore, loanSubType) {
    super(form, globalStore);
    this.globalStore = globalStore;
    this.loanSubType = loanSubType;
    this.appraisalService = new AppraisalsService();
    this.prepopulateNewProperty();

    makeObservable(this, {
      key: observable,
      isEditing: observable,
      reset: override,
    });
  }

  reset() {
    super.reset();
    this.isEditing = true;
    this.form.meta.editMode = true;
  }

  prepopulateNewProperty() {
    this.form.meta.editMode = this.loanSubType === LoanSubType.RENTAL_PORTFOLIO ? this.form.meta.editMode : true;
  }
  setEditMode(value) {
    this.form.meta.editMode = value;
    this.isEditing = value;
  }
  saveAddressField(address: string, addressDetails, geometryDetails) {
    const parsedAddress = parseAddress(
      address,
      addressDetails,
      geometryDetails
    );

    const streetAddress =
      parsedAddress.street_number === null
        ? parsedAddress.street
        : parsedAddress.street_number + ' ' + parsedAddress.street;
    this.onFieldChange('address', streetAddress);
    this.onFieldChange('streetNumber', parsedAddress.street_number);
    this.onFieldChange('streetName', parsedAddress.street);
    this.onFieldChange('city', parsedAddress.city);
    this.onFieldChange('state', parsedAddress.state);
    this.onFieldChange('county', parsedAddress.county);
    this.onFieldChange('zipCode', parsedAddress.zip);
    this.onFieldChange('latitude', parsedAddress.latitude);
    this.onFieldChange('longitude', parsedAddress.longitude);
    this.onFieldChange('aptNumber', parsedAddress.aptNumber);
  }

  /**
   * Validates the property fields and cleans up unused fields (fields hidden because of other fields values)
   */
  validateAndCleanupProperty(message?: string) {
    this.cleanupUnusedFields();
    const state = this.form.fields.state.value;
    const city = this.form.fields.city.value;
    const propertyType = this.form.fields.propertyType.value;
    const county = this.form.fields.county.value?.toLowerCase();

    if (message) {
      this.runFormValidationWithMessage(message);
    } else {
      this.runFormValidation();
    }

    return this.form.meta.isValid;
  }

  getDescribedRenovation(property: any) {
    if (this.loanSubType === LoanSubType.GROUND_UP) {
      return NEW_CONSTRUCTION;
    } else {
      const describedReno =
        property?.describeRenovation &&
          Object.values(RenoDictionary).includes(property.describeRenovation)
          ? property.describeRenovation
          : RenoDictionary[property?.squareFootageExpansion];
      return describedReno || DescribedRennovation.NO;
    }
  }

  /**
   * Cleans up unused fields (fields hidden because of other fields values)
   */
  cleanupUnusedFields() {
    const property = this.getProperty();
    this.reset();
    this.loadProperty(property);
  }

  validateProperty(prefight?: boolean) {
    this.onFieldChange('isPreflight', Boolean(prefight));
    this.runFormValidationWithMessage();
    return this.form.meta.isValid;
  }

  onFieldChange(field, value) {
    super.onFieldChange(field, value);
    if (field == 'loanPurpose') {
      if (value == REFINANCE) {
        super.onFieldChange('propertySourcing', '');
        super.onFieldChange('sellerCredit', '');
        super.onFieldChange('sellerCreditAmount', '');
        super.onFieldChange('borrowerKnowOwnerOfRecord', '');
        super.onFieldChange('armsLengthComment', '');
        super.onFieldChange('wholesalerPropertyContractAmount', '');
        super.onFieldChange('propertySourcingExplanation', '');
        if (this.loanSubType == LoanSubType.GROUND_UP) {
          super.onFieldChange('subdividedIntoLots', '');
          super.onFieldChange('numberOfLots', '');
        } else if (this.loanSubType == LoanSubType.STABILIZED_BRIDGE) {
          super.onFieldChange('squareFootageExpansion', '');
        }
      }
      if (value == PURCHASE) {
        super.onFieldChange('purchaseDate', null);
        super.onFieldChange('outstandingLoanOnTheProperty', '');
        super.onFieldChange('payoffAmount', '');
        super.onFieldChange('loanEverBeenInDefault', '');
        super.onFieldChange('constructionRenoSpendToDate', '');
        if (this.loanSubType == LoanSubType.FIX_AND_FLIP) {
          super.onFieldChange('currentMarketValue', '');
        } else if (this.loanSubType == LoanSubType.GROUND_UP) {
          super.onFieldChange('currentMarketValue', '');
          super.onFieldChange('softCostsOnly', '');
        }
      }
    }
  }

  loadProperty(property) {
    const data = {
      propertyId: property.propertyId,
      uuid: property.uuid,
      propertyType: property.propertyType,
      propertyTypeDescription: property.propertyTypeDescription,
      units: property.units,
      loanPurpose: property.loanPurpose,
      propertySourcing: property.propertySourcing,
      borrowerKnowOwnerOfRecord: property.armsLength,
      armsLengthComment: property.armsLengthComment,
      numberOfLots: property.numberOfLots,
      propertySourcingExplanation: property.propertySourcingExplanation,
      address: property.address?.addressLine1,
      aptNumber: property.address?.addressLine2,
      city: property.address?.city,
      state: property.address?.state,
      zipCode: property.address?.zipCode,
      latitude: property.address?.latitude,
      longitude: property.address?.longitude,
      purchaseDate: property.dates?.purchaseDate,
      purchasePrice: property.financials?.purchasePrice,
      currentMarketValue: property.financials?.currentMarketValue,
      renovationBudget: property.financials?.renovationBudget,
      payoffAmount: property.financials?.payoffAmount,
      marketValueAfterCompletion:
        property.financials?.marketValueAfterCompletion,
      monthlyMarketRentProvidedAtOrigination:
        property.financials?.monthlyMarketRent,
      annualTaxesProvidedAtOrigination: property.financials?.annualTaxes,
      annualInsuranceProvidedAtOrigination:
        property.financials?.annualInsurance,

      currentlyOccupied: property.flags?.currentlyOccupied,
      section8: property.flags?.section8,
      permitsInPlace: property.flags?.permitsInPlace,
      fireDamage: property.flags?.fireDamage,
      loanEverBeenInDefault: property.flags?.loanEverBeenInDefault,
      subdividedIntoLots: property.flags?.subdividedIntoLots,
      softCostsOnly: property.flags?.softCostsOnly,

      describeRenovation: property.renovation?.describeRenovation,
      squareFootageExpansion: property.renovation?.squareFootageExpansion,
      constructionRenoSpendToDate: property.renovation?.capitalImprovements,
      isAppraisalAvailable: property.valuation?.isAppraisalAvailable,
      medianZipCodeValue: property.valuation?.medianZipCodeValue,
      outstandingLoanOnTheProperty: property.outstandingLoanOnTheProperty ?? '',
      sellerCredit: property.sellerCredit,
      sellerCreditAmount: property.sellerCreditAmount,
      wholesalerPropertyContractAmount: property.financials?.wholesalerPropertyContractAmount ?? '',
    };
    this.loadForm(data);
    this.key = property.uuid;
  }

  getProperty() {
    const values = this.getFormValues();
    const isPurchase = values.loanPurpose === PURCHASE;
    const isRefinance = values.loanPurpose === REFINANCE;
    const describeRenovation =
      this.loanSubType === LoanSubType.GROUND_UP
        ? NEW_CONSTRUCTION
        : values.describeRenovation;
    const isAddingSquareFootage = DescribedRennovation.NO != describeRenovation;
    const isOtherPropertyType = values.propertyType === PROPERTY_TYPE_OTHER;

    return {
      propertyId: values.propertyId,
      uuid: this.key,
      propertyType: values.propertyType,
      propertyTypeDescription: values.propertyTypeDescription,
      units: isOtherPropertyType ? values.units : getUnitsByPropertyType(values.propertyType),
      loanPurpose: values.loanPurpose,
      propertySourcing: values.propertySourcing,
      outstandingLoanOnTheProperty: isRefinance
        ? values.outstandingLoanOnTheProperty
        : null,
      armsLength: isPurchase ? values.borrowerKnowOwnerOfRecord : null,
      armsLengthComment: isPurchase ? values.armsLengthComment : null,
      sellerCredit: isPurchase ? values.sellerCredit : null,
      sellerCreditAmount: isPurchase ? values.sellerCreditAmount : null,
      numberOfLots: isPurchase ? values.numberOfLots : null,
      propertySourcingExplanation: values.propertySourcingExplanation,

      address: {
        addressLine1: values.address,
        addressLine2: values.aptNumber,
        city: values.city,
        state: values.state,
        zipCode: values.zipCode,
        latitude: values.latitude,
        longitude: values.longitude,
      },
      dates: {
        purchaseDate: isRefinance
          ? formatDate(values.purchaseDate, DateFormat.MMDDYYYY)
          : null,
      },
      financials: {
        purchasePrice: values.purchasePrice,
        currentMarketValue: values.currentMarketValue,
        renovationBudget: values.renovationBudget,
        marketValueAfterCompletion: values.marketValueAfterCompletion,
        monthlyMarketRent: values.monthlyMarketRentProvidedAtOrigination,
        annualTaxes: values.annualTaxesProvidedAtOrigination,
        annualInsurance: values.annualInsuranceProvidedAtOrigination,
        payoffAmount: values.payoffAmount,
        wholesalerPropertyContractAmount: values.wholesalerPropertyContractAmount,
      },
      flags: {
        currentlyOccupied: values.currentlyOccupied,
        section8: values.section8,
        permitsInPlace: isAddingSquareFootage
          ? values.permitsInPlace
          : undefined,
        fireDamage: describeRenovation
          ? describeRenovation == DescribedRennovation.FIRE_DAMAGE
          : values.fireDamage,
        loanEverBeenInDefault: isRefinance
          ? values.loanEverBeenInDefault
          : null,
        subdividedIntoLots: isPurchase ? values.subdividedIntoLots : null,
        softCostsOnly: isRefinance ? values.softCostsOnly : null,
      },
      renovation: {
        describeRenovation: describeRenovation,
        squareFootageExpansion: values.squareFootageExpansion,
        capitalImprovements: values.constructionRenoSpendToDate,
      },
      valuation: {
        isAppraisalAvailable: values.isAppraisalAvailable,
        appraisalType: null,
        isSecondAppraisalRequired: null,
        medianZipCodeValue: values.medianZipCodeValue,
      },
    };
  }
}
